/* eslint-disable max-lines */
import { slotObjectToDataTestIds } from '@elliemae/ds-system';

export const DSControlledDateTimePickerName = 'DSControlleddatetimepicker';

// we are naming this with the ${component_name}_slots convention to namespace & avoid errors on duplicate exports variables in aggregators
export const FORM_DATE_TIME_PICKER_SLOTS = {
  CONTAINER: 'root',
  // THE SLOT IS ABOUT "DATE" EVEN IF THE STRING SAYS "time-..."
  //  When this was first defined there was a typo in the string, now we can't change because it is a deliverable and would impact application space.
  DATE_INPUTS_MONTH: 'time-picker-month-input',
  DATE_INPUTS_DAY: 'time-picker-day-input',
  DATE_INPUTS_YEAR: 'time-picker-year-input',
  // end of typo
  TIME_INPUTS_HOUR: 'time-picker-hour-input',
  TIME_INPUTS_MINUTE: 'time-picker-minute-input',
  TIME_INPUTS_MERIDIEM: 'time-picker-meridiem-input',
  PICKER_ICONS_CALENDAR: 'calendar-trigger-icon',
  PICKER_ICONS_TIMEWHEEL: 'timewheel-trigger-icon',
  PICKER_ICONS_CALENDAR_TIMEWHEEL: 'calendar-timewheel-trigger-icon',
  CALENDAR_DAY_BUTTON: 'calendar-day',
  CALENDAR_HEADER_LABEL: 'calendar-header-label',
  CALENDAR_HEADER_BUTTON: 'calendar-header-buttons',
  CALENDAR_HEADER_WEEKLY_DAY_LIST: 'calendar-header-weekly-day-list',
  TIMEWHEEL_TIME_BUTTON: 'timewheel-time-button',
  TIMEWHEEL_TIME_CHANGE_BUTTON: 'timewheel-time-change-button',
  TIMEWHEEL_HEADER_LABEL: 'timewheel-header-label',
  CONTEXTUAL_MENU_CALENDAR: 'calendar-context-content-wrapper',
  CONTEXTUAL_MENU_TIMEWHEEL: 'timewheel-context-content-wrapper',
  CONTEXTUAL_MENU_CALENDAR_TIMEWHEEL: 'calendar-timewheel-context-content-wrapper',
  FOOTERS_CALENDAR: 'calendar-context-footer',
  FOOTERS_TIMEWHEEL: 'timewheel-context-footer',
  FOOTERS_CALENDAR_TIMEWHEEL: 'calendar-timewheel-context-footer',
  CONTROLLER_COMPONENT_CALENDAR: 'calendar-controller-wrapper',
  CONTROLLER_COMPONENT_TIMEWHEEL: 'timewheel-controller-wrapper',
  CONTROLLER_COMPONENT_CALENDAR_TIMEWHEEL: 'calendar-timewheel-controller-wrapper',
  CLEAR_BUTTON: 'clear-btn',
} as const;

const PRE_EXISTING_DATA_TESTID = {
  // we are keeping the old names for backward compatibility...
  CONTAINER: 'ds-controlled-date-time-picker-container',
  DATE_INPUTS_MONTH: 'ds-controlled-date-time-picker-month-input',
  DATE_INPUTS_DAY: 'ds-controlled-date-time-picker-day-input',
  DATE_INPUTS_YEAR: 'ds-controlled-date-time-picker-year-input',
  TIME_INPUTS_HOUR: 'ds-controlled-date-time-picker-hour-input',
  TIME_INPUTS_MINUTE: 'ds-controlled-date-time-picker-minute-input',
  TIME_INPUTS_MERIDIEM: 'ds-controlled-date-time-picker-meridiem-input',
  PICKER_ICONS_CALENDAR: 'ds-controlled-date-time-picker-calendar-trigger-icon',
  PICKER_ICONS_TIMEWHEEL: 'ds-controlled-date-time-picker-timewheel-trigger-icon',
  PICKER_ICONS_CALENDAR_TIMEWHEEL: 'ds-controlled-date-time-picker-calendar-timewheel-trigger-icon',
  CALENDAR_TABLE_WRAPPER: 'ds-controlled-date-time-picker-calendar-table-wrapper',
  CALENDAR_HEADER_LABEL: 'ds-controlled-date-time-picker-calendar-header-label',
  CALENDAR_PREV_YEAR: 'ds-controlled-date-time-picker-calendar-prev-year',
  CALENDAR_PREV_MONTH: 'ds-controlled-date-time-picker-calendar-prev-month',
  CALENDAR_DAY: 'ds-controlled-date-time-picker-calendar-day',
  CALENDAR_FOCUSED_DAY: 'ds-controlled-date-time-picker-calendar-focused-day',
  CALENDAR_START_RANGE_DAY: 'ds-controlled-date-time-picker-calendar-start-range-day',
  CALENDAR_IN_RANGE_DAY: 'ds-controlled-date-time-picker-calendar-in-range-day',
  CALENDAR_END_RANGE_DAY: 'ds-controlled-date-time-picker-calendar-end-range-day',
  CALENDAR_SELECTED_DAY: 'ds-controlled-date-time-picker-calendar-selected-day',
  CALENDAR_SELECTED_FOCUSED_DAY: 'ds-controlled-date-time-picker-calendar-selected-focused-day',
  CALENDAR_NEXT_MONTH: 'ds-controlled-date-time-picker-calendar-next-month',
  CALENDAR_NEXT_YEAR: 'ds-controlled-date-time-picker-calendar-next-year',
  TIMEWHEEL_HEADER_LABEL: 'ds-controlled-date-time-picker-timewheel-header-label',
  TIMEWHEEL_PREV_HOUR: 'ds-controlled-date-time-picker-timewheel-prev-hour',
  TIMEWHEEL_HOUR: 'ds-controlled-date-time-picker-timewheel-hour',
  TIMEWHEEL_CURRENT_HOUR: 'ds-controlled-date-time-picker-timewheel-current-hour',
  TIMEWHEEL_NEXT_HOUR: 'ds-controlled-date-time-picker-timewheel-next-hour',
  TIMEWHEEL_PREV_MINUTE: 'ds-controlled-date-time-picker-timewheel-prev-minute',
  TIMEWHEEL_MINUTE: 'ds-controlled-date-time-picker-timewheel-minute',
  TIMEWHEEL_CURRENT_MINUTE: 'ds-controlled-date-time-picker-timewheel-current-minute',
  TIMEWHEEL_NEXT_MINUTE: 'ds-controlled-date-time-picker-timewheel-next-minute',
  TIMEWHEEL_PREV_MERIDIEM: 'ds-controlled-date-time-picker-timewheel-prev-meridiem',
  TIMEWHEEL_MERIDIEM: 'ds-controlled-date-time-picker-timewheel-meridiem',
  TIMEWHEEL_CURRENT_MERIDIEM: 'ds-controlled-date-time-picker-timewheel-current-meridiem',
  TIMEWHEEL_NEXT_MERIDIEM: 'ds-controlled-date-time-picker-timewheel-next-meridiem',
  // MeridiemList renders a dual-layer pattern (see KNOWN_INTENTIONAL_DEVIATIONS.md DEV-001):
  //   - A11y layer: invisible semantic listbox — the real focus target, owns CURRENT_MERIDIEM above.
  //   - Visual layer: aria-hidden StyledTimeBtn elements — presentation only, no focus capability.
  //
  // CURRENT_MERIDIEM / MERIDIEM originally lived on the visual elements. When the a11y layer became
  // the focus target, CURRENT_MERIDIEM moved to the listbox. The visual elements received
  // -visual-element-suffixed testids to prevent DOM query duplication (getByTestId would otherwise
  // return two elements with the same testid and throw in most automation frameworks).
  //
  // data-dimsum-slot on StyledTimeBtn (TIMEWHEEL_TIME_BUTTON) is unaffected — theming via slots is unchanged.
  TIMEWHEEL_MERIDIEM_VISUAL_ELEMENT: 'ds-controlled-date-time-picker-timewheel-meridiem-visual-element',
  TIMEWHEEL_CURRENT_MERIDIEM_VISUAL_ELEMENT: 'ds-controlled-date-time-picker-timewheel-current-meridiem-visual-element',
  CONTEXTUAL_MENU_CALENDAR: 'ds-controlled-date-time-picker-calendar-context-content-wrapper',
  CONTEXTUAL_MENU_TIMEWHEEL: 'ds-controlled-date-time-picker-timewheel-context-content-wrapper',
  CONTEXTUAL_MENU_CALENDAR_TIMEWHEEL: 'ds-controlled-date-time-picker-calendar-timewheel-context-content-wrapper',
  CONTROLLER_COMPONENT_CALENDAR: 'ds-controlled-date-time-picker-calendar-controller-wrapper',
  CONTROLLER_COMPONENT_TIMEWHEEL: 'ds-controlled-date-time-picker-timewheel-controller-wrapper',
  CONTROLLER_COMPONENT_CALENDAR_TIMEWHEEL: 'ds-controlled-date-time-picker-calendar-timewheel-controller-wrapper',
  FOOTERS_CALENDAR: 'ds-controlled-date-time-picker-calendar-context-footer',
  FOOTERS_TIMEWHEEL: 'ds-controlled-date-time-picker-timewheel-context-footer',
  FOOTERS_CALENDAR_TIMEWHEEL: 'ds-controlled-date-time-picker-calendar-timewheel-context-footer',
  CLEAR_BUTTON: 'ds-controlled-date-time-picker-clear-btn',
} as const;

// we are naming this with the ${component_name}_data_testid convention to namespace & avoid errors on duplicate exports variables in aggregators
export const FORM_DATE_TIME_PICKER_DATA_TESTID = {
  ...slotObjectToDataTestIds(DSControlledDateTimePickerName, FORM_DATE_TIME_PICKER_SLOTS),
  ...PRE_EXISTING_DATA_TESTID,
};

/* ******************************************************************************************
This component was created before the team had a convention for data-testid and slots.
*********************************************************************************************
    START OF LEGACY CODE (kept to avoid breaking changes)
****************************************************************************************** */
export const DSControlledDateTimePickerSlotsFlatten = FORM_DATE_TIME_PICKER_SLOTS;
export const DSControlledDateTimePickerSlots = {
  CONTAINER: FORM_DATE_TIME_PICKER_SLOTS.CONTAINER,
  DATE_INPUTS: {
    MONTH: FORM_DATE_TIME_PICKER_SLOTS.DATE_INPUTS_MONTH,
    DAY: FORM_DATE_TIME_PICKER_SLOTS.DATE_INPUTS_DAY,
    YEAR: FORM_DATE_TIME_PICKER_SLOTS.DATE_INPUTS_YEAR,
  },
  TIME_INPUTS: {
    HOUR: FORM_DATE_TIME_PICKER_SLOTS.TIME_INPUTS_HOUR,
    MINUTE: FORM_DATE_TIME_PICKER_SLOTS.TIME_INPUTS_MINUTE,
    MERIDIEM: FORM_DATE_TIME_PICKER_SLOTS.TIME_INPUTS_MERIDIEM,
  },
  PICKER_ICONS: {
    CALENDAR: FORM_DATE_TIME_PICKER_SLOTS.PICKER_ICONS_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.PICKER_ICONS_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.PICKER_ICONS_CALENDAR_TIMEWHEEL,
  },
  CALENDAR: {
    DAY_BUTTON: FORM_DATE_TIME_PICKER_SLOTS.CALENDAR_DAY_BUTTON,
    HEADER_LABEL: FORM_DATE_TIME_PICKER_SLOTS.CALENDAR_HEADER_LABEL,
    HEADER_BUTTON: FORM_DATE_TIME_PICKER_SLOTS.CALENDAR_HEADER_BUTTON,
    HEADER_WEEKLY_DAY_LIST: FORM_DATE_TIME_PICKER_SLOTS.CALENDAR_HEADER_WEEKLY_DAY_LIST,
  },
  TIMEWHEEL: {
    TIME_BUTTON: FORM_DATE_TIME_PICKER_SLOTS.TIMEWHEEL_TIME_BUTTON,
    TIME_CHANGE_BUTTON: FORM_DATE_TIME_PICKER_SLOTS.TIMEWHEEL_TIME_CHANGE_BUTTON,
    HEADER_LABEL: FORM_DATE_TIME_PICKER_SLOTS.TIMEWHEEL_HEADER_LABEL,
  },
  CONTEXTUAL_MENU: {
    CALENDAR: FORM_DATE_TIME_PICKER_SLOTS.CONTEXTUAL_MENU_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.CONTEXTUAL_MENU_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.CONTEXTUAL_MENU_CALENDAR_TIMEWHEEL,
  },
  FOOTERS: {
    CALENDAR: FORM_DATE_TIME_PICKER_SLOTS.FOOTERS_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.FOOTERS_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.FOOTERS_CALENDAR_TIMEWHEEL,
  },
  CONTROLLER_COMPONENT: {
    CALENDAR: FORM_DATE_TIME_PICKER_SLOTS.CONTROLLER_COMPONENT_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.CONTROLLER_COMPONENT_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_SLOTS.CONTROLLER_COMPONENT_CALENDAR_TIMEWHEEL,
  },
  CLEAR_BUTTON: FORM_DATE_TIME_PICKER_SLOTS.CLEAR_BUTTON,
};
export const ControlledDateTimePickerDatatestidFlattened = FORM_DATE_TIME_PICKER_DATA_TESTID;
export const ControlledDateTimePickerDatatestid = {
  CONTAINER: FORM_DATE_TIME_PICKER_DATA_TESTID.CONTAINER,
  DATE_INPUTS: {
    MONTH: FORM_DATE_TIME_PICKER_DATA_TESTID.DATE_INPUTS_MONTH,
    DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.DATE_INPUTS_DAY,
    YEAR: FORM_DATE_TIME_PICKER_DATA_TESTID.DATE_INPUTS_YEAR,
  },
  TIME_INPUTS: {
    HOUR: FORM_DATE_TIME_PICKER_DATA_TESTID.TIME_INPUTS_HOUR,
    MINUTE: FORM_DATE_TIME_PICKER_DATA_TESTID.TIME_INPUTS_MINUTE,
    MERIDIEM: FORM_DATE_TIME_PICKER_DATA_TESTID.TIME_INPUTS_MERIDIEM,
  },
  PICKER_ICONS: {
    CALENDAR: FORM_DATE_TIME_PICKER_DATA_TESTID.PICKER_ICONS_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.PICKER_ICONS_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.PICKER_ICONS_CALENDAR_TIMEWHEEL,
  },
  CALENDAR: {
    TABLE_WRAPPER: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_TABLE_WRAPPER,
    HEADER_LABEL: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_HEADER_LABEL,
    PREV_YEAR: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_PREV_YEAR,
    PREV_MONTH: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_PREV_MONTH,
    DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_DAY,
    FOCUSED_DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_FOCUSED_DAY,
    START_RANGE_DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_START_RANGE_DAY,
    IN_RANGE_DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_IN_RANGE_DAY,
    END_RANGE_DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_END_RANGE_DAY,
    SELECTED_DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_SELECTED_DAY,
    SELECTED_FOCUSED_DAY: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_SELECTED_FOCUSED_DAY,
    NEXT_MONTH: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_NEXT_MONTH,
    NEXT_YEAR: FORM_DATE_TIME_PICKER_DATA_TESTID.CALENDAR_NEXT_YEAR,
  },
  TIMEWHEEL: {
    HEADER_LABEL: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_HEADER_LABEL,
    PREV_HOUR: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_PREV_HOUR,
    HOUR: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_HOUR,
    CURRENT_HOUR: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_CURRENT_HOUR,
    NEXT_HOUR: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_NEXT_HOUR,
    PREV_MINUTE: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_PREV_MINUTE,
    MINUTE: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_MINUTE,
    CURRENT_MINUTE: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_CURRENT_MINUTE,
    NEXT_MINUTE: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_NEXT_MINUTE,
    PREV_MERIDIEM: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_PREV_MERIDIEM,
    MERIDIEM: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_MERIDIEM,
    CURRENT_MERIDIEM: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_CURRENT_MERIDIEM,
    NEXT_MERIDIEM: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_NEXT_MERIDIEM,
    MERIDIEM_VISUAL_ELEMENT: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_MERIDIEM_VISUAL_ELEMENT,
    CURRENT_MERIDIEM_VISUAL_ELEMENT: FORM_DATE_TIME_PICKER_DATA_TESTID.TIMEWHEEL_CURRENT_MERIDIEM_VISUAL_ELEMENT,
  },
  CONTEXTUAL_MENU: {
    CALENDAR: FORM_DATE_TIME_PICKER_DATA_TESTID.CONTEXTUAL_MENU_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.CONTEXTUAL_MENU_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.CONTEXTUAL_MENU_CALENDAR_TIMEWHEEL,
  },
  CONTROLLER_COMPONENT: {
    CALENDAR: FORM_DATE_TIME_PICKER_DATA_TESTID.CONTROLLER_COMPONENT_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.CONTROLLER_COMPONENT_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.CONTROLLER_COMPONENT_CALENDAR_TIMEWHEEL,
  },
  FOOTERS: {
    CALENDAR: FORM_DATE_TIME_PICKER_DATA_TESTID.FOOTERS_CALENDAR,
    TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.FOOTERS_TIMEWHEEL,
    CALENDAR_TIMEWHEEL: FORM_DATE_TIME_PICKER_DATA_TESTID.FOOTERS_CALENDAR_TIMEWHEEL,
  },
  CLEAR_BUTTON: FORM_DATE_TIME_PICKER_DATA_TESTID.CLEAR_BUTTON,
} as const;
/* ******************************************************************************************
    END OF LEGACY CODE (kept to avoid breaking changes)
****************************************************************************************** */
